import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Vote as VoteIcon, Coins, Trophy, Radio } from "lucide-react";
import { endpoints } from "@/lib/api";

const Badge = ({ status }) => {
  const map = { open: ["Open now", "bg-emerald-500/15 text-emerald-400"], closed: ["Closed", "bg-slate-600/30 text-slate-300"], scheduled: ["Starting soon", "bg-amber-500/15 text-amber-400"] };
  const [label, cls] = map[status] || map.open;
  return <span className={`text-[10px] font-extrabold uppercase px-2 py-1 rounded ${cls}`}>{label}</span>;
};

export default function Vote() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = () => endpoints.voteCampaigns().then((r) => setCampaigns(r.data.campaigns || [])).catch(() => {}).finally(() => setLoading(false));
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-10">
      <div className="flex items-center gap-2 mb-2">
        <VoteIcon className="w-7 h-7 text-[#E11D2A]" />
        <h1 className="text-3xl sm:text-4xl font-extrabold text-white">Vote</h1>
      </div>
      <p className="text-slate-400 text-sm mb-8">Support your favourites and shape what happens next on B&B KIGALI.</p>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{[0, 1, 2].map((i) => <div key={i} className="aspect-[4/3] rounded-2xl bg-[#121620] animate-pulse" />)}</div>
      ) : campaigns.length === 0 ? (
        <div className="text-center py-20 text-slate-500" data-testid="vote-empty">No voting campaigns are live right now. Check back soon!</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="vote-campaigns">
          {campaigns.map((c) => {
            const leader = c.options?.[0];
            return (
              <Link key={c.id} to={`/vote/${c.id}`} data-testid={`vote-card-${c.id}`}
                className="group rounded-2xl overflow-hidden bg-[#121620] border border-[#273042] card-hover flex flex-col">
                <div className="aspect-video relative overflow-hidden bg-[#0A0C10]">
                  <img src={c.coverUrl || "/bb-logo.png"} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={(e) => { e.currentTarget.src = "/bb-logo.png"; }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute top-3 left-3 flex items-center gap-2"><Badge status={c.status} />
                    {c.type === "paid" && <span className="text-[10px] font-extrabold uppercase px-2 py-1 rounded bg-[#F5B301]/20 text-[#F5B301] flex items-center gap-1"><Coins className="w-3 h-3" /> Paid</span>}
                    {c.isReward && <span className="text-[10px] font-extrabold uppercase px-2 py-1 rounded bg-[#2563EB]/20 text-[#93B4FF] flex items-center gap-1"><Trophy className="w-3 h-3" /> Reward</span>}
                  </div>
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <div className="text-white font-bold">{c.title}</div>
                  <div className="text-xs text-slate-400 line-clamp-2 mt-1 flex-1">{c.description}</div>
                  <div className="flex items-center justify-between mt-3 text-xs">
                    <span className="text-slate-400">{(c.totalVotes ?? 0).toLocaleString()} votes</span>
                    {leader && <span className="text-[#E11D2A] font-bold truncate max-w-[55%]">🏆 {leader.name}</span>}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      <div className="mt-10 flex justify-center">
        <Link to="/radio" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white"><Radio className="w-4 h-4 text-[#E11D2A]" /> Back to radio</Link>
      </div>
    </div>
  );
}
